/* ============================================================================
   jd-filter-client — PROTOTYPE fallback for the Filter & Sort panel
   ----------------------------------------------------------------------------
   Loaded by sections/product-counter.liquid only while Search & Discovery does
   not serve the three metafield filters. Shopify then ignores the filter params
   in the URL and renders the whole collection, so the filtering happens here:

     1. read the facet index (#jdf-index) and the filter params in the URL
     2. publish window.JDF — jd-jewelry-grid.js asks it which cards to keep,
        including the cards infinite scroll fetches later
     3. build the panel's Material / Category / Gemstone / Motif groups with live counts
        (and count/apply "Available online", which Liquid renders in Sort by)
     4. apply a tick at once: the grid re-filters in place (JDF.onChange) and the
        URL is rewritten to match

   The params in that URL are Shopify's own, so a reload, a shared link and sort_by
   behave as in the native version — and a link made here keeps working once S&D is
   switched on and this file is deleted.

   Runs un-deferred, straight after the panel markup: window.JDF must exist before
   jd-jewelry-grid.js (further down the page) touches the grid.
   ============================================================================ */
(function () {
  const indexEl = document.getElementById('jdf-index');
  const panel = document.querySelector('.jdf-panel[data-jdf-mode="client"]');
  if (!indexEl || !panel) return;

  const host = panel.querySelector('[data-jdf-client-groups]');
  let rows, labels, order;
  try {
    rows = JSON.parse(indexEl.textContent);
    labels = JSON.parse(host.dataset.labels);
    // The merchant's value order per group, same arrays the native loop sorts by. Optional:
    // without it the lists fall back to alphabetical.
    order = JSON.parse(host.dataset.order || '{}');
  } catch (error) {
    return;
  }

  // Group order is the order of the labels: Material, Category, Gemstones, Motif.
  const PARAMS = Object.keys(labels);

  // "Available online" — Shopify's availability filter, rendered in the Sort by group.
  // It is not one of the metafield groups, but it filters in place like them.
  const AVAIL = 'filter.v.availability';

  // product id -> one array of values per group, plus .available. A blank metafield comes
  // through as null; an index without the availability column counts as available.
  const facets = new Map();
  rows.forEach((row) => {
    const values = PARAMS.map((_, i) => row[i + 1] || []);
    values.available = row[PARAMS.length + 1] !== false;
    facets.set(String(row[0]), values);
  });

  /* ── MATCHING ─────────────────────────────────── */

  // Shopify's own semantics: values within a group are OR, groups are AND. Availability is
  // one more AND, carried as .avail on the selection.
  const selectionFrom = (search) => {
    const selection = PARAMS.map((param) => new Set(search.getAll(param)));
    selection.avail = search.get(AVAIL) === '1';
    return selection;
  };

  // `skip` leaves one group out — a group index, or 'avail' — which is how a value's count
  // is taken: "how many products would I get if I ticked this", given the OTHER groups.
  const matches = (values, selection, skip) =>
    (skip === 'avail' || !selection.avail || values.available) &&
    selection.every((chosen, i) => i === skip || chosen.size === 0 || values[i].some((v) => chosen.has(v)));

  const countMatching = (selection) => {
    let n = 0;
    facets.forEach((values) => { if (matches(values, selection, -1)) n++; });
    return n;
  };

  const urlSelection = selectionFrom(new URLSearchParams(window.location.search));
  let current = urlSelection;
  const isActive = (selection) => selection.avail || selection.some((chosen) => chosen.size > 0);

  // Landing on a URL that already carries the availability filter means the SERVER applied
  // it: the out-of-stock cards never reached this page, so unticking it cannot bring them
  // back in place — that one case still navigates.
  const landedOnAvail = urlSelection.avail;

  window.JDF = {
    active: isActive(current),
    total: countMatching(current),
    // An id missing from the index (a collection past 250 products) never matches while a
    // filter is on — better a card too few than a card that ignores the filter.
    match(id) {
      if (!this.active) return true;
      const values = facets.get(String(id));
      return !!values && matches(values, current, -1);
    },
    // Set by jd-jewelry-grid.js when it can re-filter in place (the grid, not the mobile
    // swipe feed). Without it a tick falls back to submitting the form.
    onChange: null,
  };

  /* ── THE GROUPS ───────────────────────────────── */

  // The merchant's order (data-order), which already puts the opt-out — "No Gemstone" — last.
  // A value the list does not know sorts after the listed ones, alphabetically among its peers.
  const byOrder = (param) => (a, b) => {
    const list = order[param] || [];
    const ia = list.indexOf(a);
    const ib = list.indexOf(b);
    const ra = ia < 0 ? Infinity : ia;
    const rb = ib < 0 ? Infinity : ib;
    return ra === rb ? a.localeCompare(b) : ra - rb;
  };

  const options = [];   // { group, value, label, input } for the count refresh
  PARAMS.forEach((param, group) => {
    const values = new Set();
    facets.forEach((v) => v[group].forEach((value) => values.add(value)));
    // Same rules as the native path: a group with no values in THIS collection is not shown,
    // and neither is one whose single value sits on every product — it filters nothing
    // (Category on the Rings, Bracelets, Necklaces and Earrings pages). Unless that value is
    // ticked in the URL: a hidden tick could never be undone.
    if (!values.size) return;
    if (values.size === 1) {
      const [only] = values;
      const onEvery = [...facets.values()].every((v) => v[group].includes(only));
      if (onEvery && !urlSelection[group].has(only)) return;
    }

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'jdf-group';
    const legend = document.createElement('legend');
    legend.className = 'jdf-group__title';
    legend.textContent = labels[param];
    fieldset.appendChild(legend);

    Array.from(values).sort(byOrder(param)).forEach((value) => {
      const label = document.createElement('label');
      label.className = 'jdf-opt';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = param;
      input.value = value;
      input.checked = urlSelection[group].has(value);
      const text = document.createElement('span');
      text.className = 'jdf-opt__label';
      text.textContent = value;
      const count = document.createElement('span');
      count.className = 'jdf-opt__count';
      label.append(input, text, count);
      fieldset.appendChild(label);
      options.push({ group, value, label, input, count });
    });

    host.appendChild(fieldset);
  });

  // Nothing to filter on (the clothing collections): no button, same as the native path.
  if (!options.length) {
    document.querySelector('.jdf-toggle')?.remove();
    panel.remove();
    window.JDF.active = false;
    return;
  }

  /* ── BADGE + LIVE COUNTS ──────────────────────── */

  const badge = document.querySelector('.jdf-toggle [data-jdf-active]');
  const updateBadge = () => {
    if (!badge) return;
    const n = current.reduce((sum, chosen) => sum + chosen.size, 0) + (current.avail ? 1 : 0);
    badge.textContent = n;
    badge.setAttribute('aria-label', n + ' active filters');
    badge.hidden = n === 0;
  };
  updateBadge();

  const form = panel.querySelector('.jdf-form');
  const submitCount = panel.querySelector('[data-jdf-count]');
  const formSelection = () => selectionFrom(new URLSearchParams(new FormData(form)));
  const availInput = form.querySelector('input[name="' + AVAIL + '"]');
  const availCount = availInput?.closest('.jdf-opt')?.querySelector('.jdf-opt__count');

  // Answered from the index, so no round trip — the server ignores these filters here.
  const refresh = () => {
    const selection = formSelection();
    options.forEach((opt) => {
      let n = 0;
      facets.forEach((values) => {
        if (values[opt.group].includes(opt.value) && matches(values, selection, opt.group)) n++;
      });
      opt.count.textContent = n;
      const empty = n === 0 && !opt.input.checked;
      opt.input.disabled = empty;
      opt.label.classList.toggle('jdf-opt--empty', empty);
    });
    if (availInput) {
      let n = 0;
      facets.forEach((values) => { if (values.available && matches(values, selection, 'avail')) n++; });
      if (availCount) availCount.textContent = n;
      const empty = n === 0 && !availInput.checked;
      availInput.disabled = empty;
      availInput.closest('.jdf-opt')?.classList.toggle('jdf-opt--empty', empty);
    }
    if (submitCount) submitCount.textContent = countMatching(selection);
  };

  /* ── APPLY ON TICK ────────────────────────────── */

  // A tick applies at once: the grid re-filters in place and the URL follows along with
  // replaceState, so a reload or a shared link lands on the same view. Sort is the server's
  // job, so a sort change still navigates — jd-filter-panel.js then reopens the panel.
  const apply = () => {
    current = formSelection();
    window.JDF.active = isActive(current);
    window.JDF.total = countMatching(current);
    updateBadge();

    if (!window.JDF.onChange) {
      jdfReopen();
      form.requestSubmit();
      return;
    }

    const search = new URLSearchParams(new FormData(form));
    if (!search.get('sort_by')) search.delete('sort_by');
    const query = search.toString();
    window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : ''));
    window.JDF.onChange();
  };

  const jdfReopen = () => {
    try { sessionStorage.setItem('jdf-reopen', String(panel.querySelector('.jdf-body').scrollTop)); } catch (error) { /* reopening is a nicety */ }
  };

  form.addEventListener('change', (event) => {
    // Sort is the server's job, so it navigates — as does unticking an availability filter
    // the server already applied (see landedOnAvail).
    const unAvail = event.target === availInput && landedOnAvail && !availInput.checked;
    if (event.target.name === 'sort_by' || unAvail) {
      jdfReopen();
      form.requestSubmit();
      return;
    }
    refresh();
    apply();
  });

  // "Reset all filters" clears in place too; its href stays the no-JS fallback.
  panel.querySelector('.jdf-reset')?.addEventListener('click', (event) => {
    // Out-of-stock cards the server left out cannot come back in place: let the link go.
    if (landedOnAvail) return;
    event.preventDefault();
    options.forEach((opt) => { opt.input.checked = false; });
    if (availInput) availInput.checked = false;
    refresh();
    apply();
  });

  refresh();
})();
